import {ChangeDetectorRef, Injector, ViewContainerRef} from '@angular/core';
import {L10nTranslationService} from 'angular-l10n';
import * as _ from 'lodash-es';
import {EMPTY, MonoTypeOperatorFunction, Observable, pipe, Subject} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {isApiErrorArray} from '../../common/api-error-array';
import {ResponseError} from '../../common/api-helpers';
import {DynamicComponentCreationOptions} from '../dynamic-component';
import {ModalService} from '../modal';
import {AlertModalComponent} from '../modal/alert-modal';
import {COMMON_RESPONSE_ERROR_TITLE, COMMON_UNKNOWN_ERROR_TITLE} from './common-string';

export type AlertFn = (
    title: string,
    message: string,
    titleTranslate: boolean,
    messageTranslate: boolean,
    injector: Injector,
    vcRef?: ViewContainerRef,
    destroyWhen?: Observable<unknown>
) => void;

/**
 * A common error response format.
 */
export interface ResponseErrorCodeMsg {
    code: number;
    message: string;
}

const MAX_ERROR_JSON_LENGTH = 500;

export const showAlertOnResponseErrorCodeMsg = <T>(params: {
    injector: Injector;
    vcRef?: ViewContainerRef;
    errorTitle?: string;
    errorCodesL10nDict?: Record<string, string>;
    translationService?: L10nTranslationService;
    l10nParams?: Record<string, string | number>;
    alertFn?: AlertFn;
    destroyAlertSubject$?: Subject<void>;
}): MonoTypeOperatorFunction<T> => {
    const destroyPrevAlert$ = params.destroyAlertSubject$ || new Subject<void>();

    return pipe(
        catchError((error: ResponseError | ResponseErrorCodeMsg) => {
            const hasErrorMsg = !!error.message;
            const errorMsgAsString = hasErrorMsg
                && formatAnyAsErrorMessage(
                    error.message,
                    params.errorCodesL10nDict,
                    params.translationService || params.injector?.get(L10nTranslationService, null, {optional: true}),
                    params.l10nParams
                );

            const alertFn = params.alertFn || showAlert;

            destroyPrevAlert$.next();

            alertFn(
                params.errorTitle || COMMON_RESPONSE_ERROR_TITLE,
                hasErrorMsg
                    ? errorMsgAsString
                    : COMMON_UNKNOWN_ERROR_TITLE,
                true,
                !hasErrorMsg,
                params.injector,
                params.vcRef,
                destroyPrevAlert$
            );

            return EMPTY;
        })
    );
};

const showAlert: AlertFn = (
    title: string,
    message: string,
    titleTranslate: boolean,
    messageTranslate: boolean,
    injector: Injector,
    vcRef?: ViewContainerRef,
    destroyWhen?: Observable<unknown>
): void => {
    const changeDetectorRef = injector.get(ChangeDetectorRef);
    const modalService = injector.get(ModalService);
    const options: DynamicComponentCreationOptions<AlertModalComponent> = {
        injector,
        configure: {
            alertTitle:   title,
            alertMessage: message,

            alertTitleTranslate:   titleTranslate,
            alertMessageTranslate: messageTranslate
        }
    };

    if (vcRef) {
        options.place = {
            viewContainerRef: vcRef
        };
    }

    if (destroyWhen) {
        options.destroyWhen = destroyWhen;
    }

    modalService.open(AlertModalComponent, options);

    changeDetectorRef.markForCheck();
};

export const formatAnyAsErrorMessage = (
    valueToFormat: any,
    errorCodesL10nDict?: Record<string, string>,
    translationService?: L10nTranslationService,
    l10nParams?: Record<string, string | number>
): string => {
    let result: string;

    if (_.isString(valueToFormat)) {
        result = valueToFormat;
    } else if (isApiErrorArray(valueToFormat) && errorCodesL10nDict) {
        result = valueToFormat.reduce(
            (acc, item) => {
                const dictValue = errorCodesL10nDict[item];

                if (dictValue && translationService) {
                    acc.push(translationService.translate(dictValue, l10nParams));
                } else if (dictValue) {
                    acc.push(dictValue);
                } else {
                    acc.push(item);
                }

                return acc;
            },
            []
        ).join('\n');
    } else {
        const possibleErrorText = valueToFormat && valueToFormat.error && valueToFormat.error.message;

        if (possibleErrorText && _.isString(possibleErrorText)) {
            result = possibleErrorText;
        } else {
            const json = JSON.stringify(valueToFormat, null, 2);

            if (json === '{\n  "isTrusted": true\n}' && valueToFormat instanceof ProgressEvent) {
                const url = (valueToFormat.target as any).__zone_symbol__xhrURL;

                result = `ProgressEvent\ntype: ${valueToFormat.type}\nURL: ${url}`;
            } else {
                result = json.substring(0, MAX_ERROR_JSON_LENGTH);
            }
        }
    }

    return result;
};
